import { useCallback } from 'react'
import { InlineEditableCell } from '@/components/custom/inline-editable-cell'
import { useUpdateCompany } from '@/hooks/use-companies'
import { createMemoCell } from '@/lib/create-memo-cell'
import { formatFieldName } from '@/lib/formatters'
import type { UpdateCompany } from '@/types/api'

type InputType = 'text' | 'number'

interface InputCellProps {
  companyId: string
  primaryOwnerId?: string | null
  secondaryOwnerId?: string | null
  fieldName: keyof UpdateCompany
  value: string | number | null | undefined
  inputType?: InputType
  placeholder?: string
  /** Format function for display (e.g., currency formatting) */
  formatDisplay?: (value: string | number | null | undefined) => string
}

function InputCellBase({
  companyId,
  primaryOwnerId,
  secondaryOwnerId,
  fieldName,
  value,
  inputType = 'text',
  placeholder = '-',
  formatDisplay,
}: InputCellProps) {
  const updateCompany = useUpdateCompany()

  const fieldLabel = formatFieldName(fieldName)

  const handleSave = useCallback(
    async (newValue: string | number | null) => {
      const updateData: Partial<UpdateCompany> = {
        [fieldName]: newValue ?? undefined,
      }
      await updateCompany.mutateAsync({ id: companyId, data: updateData })
    },
    [companyId, fieldName, updateCompany]
  )

  return (
    <InlineEditableCell
      primaryOwnerId={primaryOwnerId}
      secondaryOwnerId={secondaryOwnerId}
      value={value}
      inputType={inputType}
      placeholder={placeholder}
      formatDisplay={formatDisplay}
      onSave={handleSave}
      successMessage={`${fieldLabel} updated`}
      errorMessage={`Failed to update ${fieldLabel.toLowerCase()}`}
    />
  )
}

export const InputCell = createMemoCell(InputCellBase, 'InputCell')
export type { InputCellProps, InputType }
